import * as React from "react";
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography
} from "../../../components/mui";
import Modal from "../../../components/shared/Modal";
import { useToast } from "../../../components/shared/ToastProvider";

/**
 * MiscExpenseFormModal
 * Reusable modal for creating/updating condo misc expenses.
 * UI-only; backend will wire form state + submission later.
 */
export default function MiscExpenseFormModal({
  open,
  onClose,
  mode = "add", // "add" | "edit"
  initialValues = {}
}) {
  const isEdit = mode === "edit";
  const { showToast } = useToast();

  const handlePrimaryClick = () => {
    // UI-only: backend will handle actual save later.
    showToast({
      message: isEdit ? "Misc expense updated" : "Misc expense created",
      severity: "success"
    });
    if (onClose) {
      onClose();
    }
  };

  const actions = (
    <>
      <Button variant="outlined" onClick={onClose}>
        Cancel
      </Button>
      <Button variant="contained" onClick={handlePrimaryClick}>
        {isEdit ? "Update" : "Create"}
      </Button>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit Condo Misc Expense" : "Add Condo Misc Expense"}
      actions={actions}
      maxWidth="sm"
    >
      <Box className="modal-form-grid" style={{ marginTop: 8 }}>
        {/* Misc Expense Type */}
        <FormControl fullWidth required size="small" className="modal-form-row">
          <InputLabel>Misc Expense Type</InputLabel>
          <Select
            defaultValue={initialValues.type || "Assoc Dues"}
            label="Misc Expense Type"
          >
            <MenuItem value="Assoc Dues">Assoc Dues</MenuItem>
            <MenuItem value="Internet">Internet</MenuItem>
            <MenuItem value="Appliance">Appliance</MenuItem>
            <MenuItem value="Start Up Supplies">Start Up Supplies</MenuItem>
            <MenuItem value="Stay In Allowance">Stay In Allowance</MenuItem>
          </Select>
        </FormControl>

        {/* Currency & Amount */}
        <Box className="modal-form-row modal-form-row--two-cols">
          <FormControl fullWidth required size="small">
            <InputLabel>Currency</InputLabel>
            <Select
              defaultValue={initialValues.currency || "PHP"}
              label="Currency"
            >
              <MenuItem value="PHP">PHP</MenuItem>
              <MenuItem value="USD">USD</MenuItem>
              <MenuItem value="KHR">KHR</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            required
            size="small"
            type="number"
            label="Amount"
            placeholder="0.00"
            defaultValue={initialValues.amountValue || ""}
            inputProps={{ step: "0.01" }}
          />
        </Box>

        {/* Payment Term & Start Date */}
        <Box className="modal-form-row modal-form-row--two-cols">
          <FormControl fullWidth size="small">
            <InputLabel>Payment Term</InputLabel>
            <Select
              defaultValue={initialValues.paymentTerm || "1 month"}
              label="Payment Term"
            >
              <MenuItem value="1 month">1 month</MenuItem>
              <MenuItem value="3 months">3 months</MenuItem>
              <MenuItem value="6 months">6 months</MenuItem>
              <MenuItem value="12 months">12 months</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            required
            size="small"
            label="Start Date"
            type="date"
            defaultValue={initialValues.startDateRaw || ""}
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        {/* Payable To */}
        <TextField
          fullWidth
          size="small"
          className="modal-form-row"
          label="Payable To"
          defaultValue={initialValues.payableTo || ""}
        />

        {/* In-Charge */}
        <FormControl fullWidth size="small" className="modal-form-row">
          <InputLabel>In-Charge</InputLabel>
          <Select
            defaultValue={initialValues.inCharge || "-"}
            label="In-Charge"
          >
            <MenuItem value="-">-</MenuItem>
            <MenuItem value="VT027 (Ken)">VT027 (Ken)</MenuItem>
          </Select>
        </FormControl>

        {/* Remarks */}
        <TextField
          fullWidth
          multiline
          rows={3}
          size="small"
          className="modal-form-row"
          label="Remarks"
          defaultValue={initialValues.remarks || ""}
        />
      </Box>
    </Modal>
  );
}
